import { fromLonLat, toLonLat, transform, get as getProjection } from 'ol/proj';

const KOREA_CENTER_3857 = fromLonLat([127, 37.5]);
const KOREA_BOUNDS = { lonMin: 119, lonMax: 133, latMin: 30, latMax: 45 };

export const isWithin3857Bounds = (x, y) => {
    const numX = Number(x);
    const numY = Number(y);
    if (!Number.isFinite(numX) || !Number.isFinite(numY)) return false;
    const minX = 10000000;
    const maxX = 15000000;
    const minY = 2500000;
    const maxY = 6000000;
    return numX >= minX && numX <= maxX && numY >= minY && numY <= maxY;
};

export const guessProjectionByRange = (x, y) => {
    const numX = Number(x);
    const numY = Number(y);
    if (!Number.isFinite(numX) || !Number.isFinite(numY)) return null;
    if (numX >= 400000 && numX <= 900000 && numY >= 200000 && numY <= 600000) {
        return 'EPSG:5181';
    }
    if (numX >= 1000000 && numX <= 2000000 && numY >= 200000 && numY <= 700000) {
        return 'EPSG:5179';
    }
    if (numX >= 500000 && numX <= 1500000 && numY >= 1500000 && numY <= 2500000) {
        return 'EPSG:5186';
    }
    return null;
};

export const toMapPoint = (record) => {
    const hasCartesian = Number.isFinite(Number(record.x3857)) && Number.isFinite(Number(record.y3857));
    if (hasCartesian) {
        return { x: Number(record.x3857), y: Number(record.y3857) };
    }
    const hasSrid3857 = String(record.srid || '').replace(/epsg:/i, '') === '3857';
    if (hasSrid3857 && isWithin3857Bounds(record.x, record.y)) {
        return { x: Number(record.x), y: Number(record.y) };
    }
    const rawX = Number(record.x);
    const rawY = Number(record.y);
    if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
    if (isWithin3857Bounds(rawX, rawY)) {
        return { x: rawX, y: rawY };
    }
    const srid = String(record.srid || '').replace(/epsg:/i, '');
    const guessedCode = guessProjectionByRange(rawX, rawY);
    const sourceCode = srid ? `EPSG:${srid}` : guessedCode;
    const candidates = [];
    const addCandidate = (pt) => {
        const [mx, my] = Array.isArray(pt) ? pt : [];
        if (Number.isFinite(mx) && Number.isFinite(my)) {
            candidates.push({ x: mx, y: my });
        }
    };
    try {
        if (srid === '4326') {
            addCandidate(fromLonLat([rawX, rawY]));
            addCandidate(fromLonLat([rawY, rawX]));
        } else if (sourceCode) {
            const projection = getProjection(sourceCode);
            if (projection) {
                addCandidate(transform([rawX, rawY], sourceCode, 'EPSG:3857'));
                addCandidate(transform([rawY, rawX], sourceCode, 'EPSG:3857'));
            }
        }
    } catch (e) {
        // ignore transform errors
    }
    if (!candidates.length) {
        addCandidate(fromLonLat([rawX, rawY]));
    }
    const isInsideKorea = ({ x, y }) => {
        const [lon, lat] = toLonLat([x, y]);
        return (
            lon >= KOREA_BOUNDS.lonMin &&
            lon <= KOREA_BOUNDS.lonMax &&
            lat >= KOREA_BOUNDS.latMin &&
            lat <= KOREA_BOUNDS.latMax
        );
    };
    const distanceToCenter = ({ x, y }) => {
        const dx = x - KOREA_CENTER_3857[0];
        const dy = y - KOREA_CENTER_3857[1];
        return dx * dx + dy * dy;
    };
    const inside = candidates.filter(isInsideKorea);
    if (inside.length === 1) return inside[0];
    if (inside.length > 1) {
        return inside.reduce((best, cur) => (distanceToCenter(cur) < distanceToCenter(best) ? cur : best));
    }
    return candidates.reduce((best, cur) => (distanceToCenter(cur) < distanceToCenter(best) ? cur : best));
};

export const normalizeRecord = (record, mapPoint) => {
    if (!mapPoint) return null;
    const safeRecord = { ...record };
    const lonLat = toLonLat([mapPoint.x, mapPoint.y]);
    return {
        id: safeRecord.id ?? Date.now(),
        mode: safeRecord.mode ?? 'forward',
        srid: safeRecord.srid || 'EPSG:3857',
        input: safeRecord.input ?? safeRecord.address ?? '',
        addrType: safeRecord.addrType ?? '',
        roadAddress: safeRecord.roadAddress ?? safeRecord.road_address ?? '',
        jibunAddress: safeRecord.jibunAddress ?? safeRecord.jibun_address ?? '',
        timestamp: safeRecord.timestamp ?? new Date().toISOString(),
        x: safeRecord.x ?? lonLat[0],
        y: safeRecord.y ?? lonLat[1],
        lon: lonLat[0],
        lat: lonLat[1],
        x3857: safeRecord.x3857 ?? mapPoint.x,
        y3857: safeRecord.y3857 ?? mapPoint.y,
        mapX: mapPoint.x,
        mapY: mapPoint.y,
        favorite: Boolean(safeRecord.favorite),
        fileName: safeRecord.fileName ?? null,
        source: safeRecord.source ?? 'manual',
    };
};
